import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { EditableSection } from "@/components/plan/DocSections";
import { cn } from "@/lib/utils";

const TONE = { "Validated": "bg-[#E8F7F0] text-[#008F4D]", "Validated with warning": "bg-amber-100 text-amber-800", "Not validated": "bg-gray-100 text-gray-600", "Safety limit exceeded": "bg-red-100 text-red-700" };

function Badge({ s, site, k, i }) {
  return <span className={cn("rounded-full px-2.5 py-0.5 font-semibold", TONE[s] || TONE["Not validated"])} data-testid={`engine-status-${k}-${site || i}`}>{site ? `${site}: ` : ""}{s}</span>;
}

function Block({ k, title, value, onSave, items, rationale, basis }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2" data-testid={`engine-${k}`}>
      <div className="flex flex-wrap gap-2">{items.map((x, i) => <Badge key={i} s={x.status} site={x.site} k={k} i={i} />)}</div>
      <p className="text-sm text-[#8B9698]">Status reflects the engine check at generation; your edits below are saved to the plan.</p>
      {items.flatMap((x) => (x.issues || []).map((m, j) => <p key={`${x.site}-${j}`} className="text-amber-800">{x.site ? `Site ${x.site}: ` : ""}{m}</p>))}
      <EditableSection letter="" title={title} k={k} value={value} onSave={onSave} />
      {rationale && <p className="text-[#3a4a4f] dark:text-[#c8d2d4]" data-testid={`engine-rationale-${k}`}><b>Reasoning:</b> {rationale}</p>}
      {basis?.length > 0 && <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 font-semibold text-[#00A85A]" data-testid="engine-basis-toggle"><ChevronDown className={cn("h-4 w-4", open && "rotate-180")} />Calculation basis</button>}
      {open && <ul className="ml-5 list-disc text-[#687173]" data-testid="engine-basis">{basis.map((b, i) => <li key={i}>{b}</li>)}</ul>}
    </div>
  );
}

export function ImplantOutcomes({ ct, onSave }) {
  const e = ct.implant_engine;
  const r = ct.implant_rationale || {};
  return (
    <section className="space-y-5 rounded-2xl border border-[#E2E8F0] bg-white p-5 dark:border-[#223139] dark:bg-[#0E1A21]" data-testid="implant-outcomes">
      <h3 className="font-bold text-[#101516] dark:text-white">Implant planning outcomes <span className="font-normal text-[#8B9698]">· engine settings v{e.settings_version || 0}</span></h3>
      <Block k="implant_outcome_size" title="1. Implant size" value={ct.implant_outcome_size} onSave={onSave} items={e.size} rationale={r.size} basis={e.size.flatMap((x) => x.basis.map((b) => `Site ${x.site}: ${b}`))} />
      <Block k="implant_outcome_hard" title="2. Hard tissue" value={ct.implant_outcome_hard} onSave={onSave} items={e.hard} rationale={r.hard_tissue} />
      <Block k="implant_outcome_soft" title="3. Soft tissue" value={ct.implant_outcome_soft} onSave={onSave} items={e.soft} rationale={r.soft_tissue} />
      {e.prosthesis && <Block k="implant_outcome_prosthesis" title="4. Prosthesis" value={ct.implant_outcome_prosthesis} onSave={onSave} items={[e.prosthesis]} rationale={r.prosthesis} />}
      <Block k="implant_final" title="Final primary recommendation" value={ct.implant_final} onSave={onSave} items={[{ status: e.final.status }]} rationale={r.final} />
    </section>
  );
}
